// Session 9: voice answers. A small mic button that sits under a text box,
// records with MediaRecorder, sends the clip to the `transcribe` Edge Function
// (the user's JWT rides along with supabase.functions.invoke) and APPENDS the
// text at the cursor of the box. It never replaces what is there and never
// submits anything: the client edits, then sends as usual.
//
// Props
//   value, onChange   the text box's state (same pair the <textarea> uses)
//   inputRef          ref to the <textarea>, for the caret position and focus
//   disabled          true while the surrounding form is busy
//   clientId          the client being viewed (agency only; the function
//                     pins clients to their own row regardless)
//   hint              show the one-line "Tap the mic and talk" hint below
//                     (pass it on the first mic of a page only)
//
// Hidden entirely when the browser has no MediaRecorder.

import { useEffect, useRef, useState } from 'react';
import { supabase } from '../supabase.js';

const MAX_SECONDS = 300;         // auto-stop (the transcribe function caps at the same)
const COUNTDOWN_FROM = 30;       // "Stopping in Ns" for the last 30 s
const MAX_BYTES = 10 * 1024 * 1024;
const MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg'];

const DENIED_MSG = 'Your browser blocked the microphone. Allow it in the address bar, then try again.';
const GENERIC_MSG = "We couldn't transcribe that. Please try again.";

export const MIC_HINT = "Tap the mic and talk — up to 5 minutes at a time; tap again to add more. Stay on this page until you've pressed Send.";

export function micSupported() {
  return typeof window !== 'undefined'
    && typeof window.MediaRecorder === 'function'
    && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
}

function pickMime() {
  for (const m of MIME_CANDIDATES) {
    try { if (window.MediaRecorder.isTypeSupported(m)) return m; } catch { /* keep looking */ }
  }
  return '';
}

function extFor(mime) {
  const t = (mime || '').split(';')[0].trim().toLowerCase();
  if (t.includes('mp4') || t.includes('m4a')) return 'm4a';
  if (t.includes('ogg')) return 'ogg';
  if (t.includes('wav')) return 'wav';
  if (t.includes('mpeg') || t.includes('mp3')) return 'mp3';
  return 'webm';
}

// Insert `text` at the caret of `el` (or at the end), keeping one space
// between the new text and its neighbours.
export function insertAtCaret(value, text, el) {
  const v = String(value || '');
  const hasSel = el && typeof el.selectionStart === 'number' && typeof el.selectionEnd === 'number';
  const start = hasSel ? Math.min(el.selectionStart, v.length) : v.length;
  const end = hasSel ? Math.min(Math.max(el.selectionEnd, start), v.length) : start;
  const before = v.slice(0, start);
  const after = v.slice(end);
  const t = String(text || '').trim();
  const lead = before && !/\s$/.test(before) ? ' ' : '';
  const trail = after && !/^\s/.test(after) ? ' ' : '';
  const insert = lead + t + trail;
  return { next: before + insert + after, caret: before.length + insert.length };
}

function fmtClock(s) {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

const AUTO_STOP_MSG = `Recording stopped at ${fmtClock(MAX_SECONDS)}. Tap the mic again to keep going.`;

async function readFunctionError(error) {
  // supabase-js wraps a non-2xx as FunctionsHttpError with the Response on
  // .context; the function always answers { error: "<plain sentence>" }.
  try {
    const ctx = error && error.context;
    if (ctx && typeof ctx.json === 'function') {
      const body = await ctx.clone().json();
      if (body && typeof body.error === 'string' && body.error.trim()) return body.error.trim();
    }
  } catch { /* fall through */ }
  return GENERIC_MSG;
}

export default function MicButton({ value, onChange, inputRef, disabled = false, clientId, hint = false }) {
  const [state, setState] = useState('idle');       // idle | recording | transcribing | error
  const [elapsed, setElapsed] = useState(0);
  const [errMsg, setErrMsg] = useState('');
  const [autoStopped, setAutoStopped] = useState(false); // the last clip hit MAX_SECONDS

  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const tickRef = useRef(null);
  const startedAtRef = useRef(0);
  const stoppingRef = useRef(false);
  const mountedRef = useRef(true);
  const latestRef = useRef({ value, onChange });
  latestRef.current = { value, onChange };

  const supported = micSupported();

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      teardown();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function teardown() {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') { try { rec.onstop = null; rec.stop(); } catch { /* already stopped */ } }
    recorderRef.current = null;
    const stream = streamRef.current;
    if (stream) { try { stream.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ } }
    streamRef.current = null;
    chunksRef.current = [];
  }

  function showError(msg) {
    if (!mountedRef.current) return;
    setErrMsg(msg || GENERIC_MSG);
    setState('error');
  }

  async function start() {
    if (disabled || state === 'recording' || state === 'transcribing') return;
    setErrMsg('');
    setAutoStopped(false);
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      const name = e && e.name;
      if (name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError') return showError(DENIED_MSG);
      if (name === 'NotFoundError' || name === 'DevicesNotFoundError') return showError("We couldn't find a microphone on this device.");
      if (name === 'NotReadableError' || name === 'AbortError') return showError('Another app is using the microphone. Close it, then try again.');
      return showError(DENIED_MSG);
    }
    if (!mountedRef.current) { stream.getTracks().forEach((t) => t.stop()); return; }

    let recorder;
    try {
      const mime = pickMime();
      recorder = mime ? new window.MediaRecorder(stream, { mimeType: mime }) : new window.MediaRecorder(stream);
    } catch {
      stream.getTracks().forEach((t) => t.stop());
      return showError("This browser can't record audio here. Please type your answer.");
    }

    streamRef.current = stream;
    recorderRef.current = recorder;
    chunksRef.current = [];
    stoppingRef.current = false;

    recorder.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data); };
    recorder.onerror = () => { teardown(); showError("Recording stopped unexpectedly. Please try again."); };
    recorder.onstop = () => {
      const mime = recorder.mimeType || pickMime() || 'audio/webm';
      const blob = new Blob(chunksRef.current, { type: mime });
      const seconds = Math.max(0, Math.round((Date.now() - startedAtRef.current) / 100) / 10);
      const stream2 = streamRef.current;
      if (stream2) { try { stream2.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ } }
      streamRef.current = null;
      recorderRef.current = null;
      chunksRef.current = [];
      if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
      void transcribe(blob, seconds);
    };

    startedAtRef.current = Date.now();
    setElapsed(0);
    setState('recording');
    try {
      recorder.start(1000);
    } catch {
      teardown();
      return showError("This browser can't record audio here. Please type your answer.");
    }

    tickRef.current = setInterval(() => {
      const s = Math.floor((Date.now() - startedAtRef.current) / 1000);
      if (!mountedRef.current) return;
      setElapsed(s);
      if (s >= MAX_SECONDS) { setAutoStopped(true); stop(); }
    }, 250);
  }

  function stop() {
    if (stoppingRef.current) return;
    const rec = recorderRef.current;
    if (!rec || rec.state === 'inactive') return;
    stoppingRef.current = true;
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    try { rec.stop(); } catch { teardown(); showError(GENERIC_MSG); }
  }

  async function transcribe(blob, seconds) {
    if (!mountedRef.current) return;
    if (!blob || blob.size === 0) return showError("We didn't pick up any audio. Check your microphone and try again.");
    if (blob.size > MAX_BYTES) return showError('That recording is too large (over 10 MB). Try a shorter one.');
    setState('transcribing');

    const fd = new FormData();
    fd.append('audio', blob, `answer.${extFor(blob.type)}`);
    fd.append('duration', String(seconds));
    if (clientId) fd.append('client_id', String(clientId));

    let text = '';
    try {
      const { data, error } = await supabase.functions.invoke('transcribe', { body: fd });
      if (error) return showError(await readFunctionError(error));
      if (data && typeof data.error === 'string' && data.error) return showError(data.error);
      text = data && typeof data.text === 'string' ? data.text.trim() : '';
    } catch {
      return showError(GENERIC_MSG);
    }
    if (!mountedRef.current) return;
    if (!text) return showError("We didn't catch any words. Try again a little closer to the microphone.");

    const el = inputRef && inputRef.current;
    const { value: cur, onChange: set } = latestRef.current;
    const { next, caret } = insertAtCaret(cur, text, el);
    set(next);
    setState('idle');
    setElapsed(0);
    if (el) {
      // Let React paint the new value before moving the caret after it.
      setTimeout(() => {
        try { el.focus(); el.setSelectionRange(caret, caret); } catch { /* not focusable right now */ }
      }, 0);
    }
  }

  if (!supported) return null;

  const remaining = MAX_SECONDS - elapsed;
  const countdown = state === 'recording' && remaining <= COUNTDOWN_FROM && remaining > 0;

  return (
    <div className="mic">
      <div className="mic-row">
        {state === 'idle' && (
          <button type="button" className="mic-btn" aria-label="Speak your answer" onClick={start} disabled={disabled}>
            <MicIcon />
            <span>Speak</span>
          </button>
        )}

        {state === 'recording' && (
          <>
            <span className="mic-live" role="status" aria-live="polite">
              <span className="mic-dot" aria-hidden="true" />
              <span className="mic-time" aria-label={`Recording, ${fmtClock(elapsed)}`}>{fmtClock(elapsed)}</span>
              {countdown && <span className="mic-countdown">Stopping in {remaining}s</span>}
            </span>
            <button type="button" className="btn mic-stop" onClick={stop}>Stop</button>
          </>
        )}

        {state === 'transcribing' && (
          <span className="mic-live" role="status" aria-live="polite">
            <span className="spinner" aria-hidden="true" />
            <span>Transcribing…</span>
          </span>
        )}

        {autoStopped && (state === 'transcribing' || state === 'idle') && (
          <span className="mic-note" role="status">{AUTO_STOP_MSG}</span>
        )}

        {state === 'error' && (
          <>
            <span className="mic-err" role="alert">{errMsg}</span>
            <button type="button" className="link-btn" onClick={start} disabled={disabled}>Try again</button>
          </>
        )}
      </div>
      {hint && <p className="hint mic-hint">{MIC_HINT}</p>}
    </div>
  );
}

function MicIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <rect x="9" y="3" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <line x1="12" y1="18" x2="12" y2="21" />
      <line x1="9" y1="21" x2="15" y2="21" />
    </svg>
  );
}
